/* eslint-disable no-param-reassign */
import { createReducer } from '@reduxjs/toolkit';

// Product loading and CRUD
import { reducer as getProducts } from './getProducts';
import { reducer as create } from './create';
import { reducer as search } from './search';
import { reducer as rename } from './rename';
import { reducer as remove } from './remove';
import { reducer as pullProduct } from './getMasterProduct';
import { reducer as pullProductFromMaster } from './pullProductFromMaster';
import { reducer as removeMasterProduct } from './removeMasterProduct';
import { reducer as removeMasterVariant } from './removeMasterVariant';
// Master product fields
import { reducer as addKeywords } from './addKeywords';
import { reducer as removeKeywords } from './removeKeywords';
import { reducer as archiveListing } from './archiveListing';
import { reducer as changeStatus } from './changeStatus';
import { reducer as changeDescription } from './changeDescription';
import { reducer as updateMasterVariant } from './updateMasterVariant';
import { reducer as changeHandle } from './changeHandle';
import { reducer as changeOrder } from './changeOrder';
import { reducer as setUsageAltText } from './setUsageAltText';
import { reducer as patchAssetAltText } from './patchAssetAltText';
import { reducer as updateMasterProduct } from './updateMasterProduct';
import { reducer as changeProductCustomField } from './changeProductCustomField';
import { reducer as changeVariantCustomField } from './changeVariantCustomField';
// Options and master variants
import { reducer as getVariants } from './getVariants';
import { reducer as createOption } from './createOption';
import { reducer as deleteOption } from './deleteOption';
import { reducer as createOptionValue } from './createOptionValue';
import { reducer as deleteOptionValue } from './deleteOptionValue';
import { reducer as createVariant } from './createVariant';
// Product assets and media
import { reducer as addAssets } from './addAssets';
import { reducer as attachAssetsToTargets } from './attachAssetsToTargets';
import { reducer as removeAssets } from './removeAssets';
import { reducer as detachAssetsFromTargets } from './detachAssetsFromTargets';
import { reducer as fetchAssets } from './fetchAssets';
import { reducer as fetchVariantAssets } from './fetchVariantAssets';
import { reducer as removeAssetFromVariant } from './removeAssetFromVariant';
import { reducer as detachProductVariantFromAssets } from './detachProductVariantFromAssets';
import { reducer as fetchThumbnails } from './fetchThumbnails';
import { reducer as updateAssetFields } from './updateMediaFields';
import { reducer as setMediaThumbnails } from './setMediaThumbnails';
import { reducer as fetchListingAssets } from './fetchListingAssets';
// Channel/listing selection
import { reducer as setSelectedProductChannel } from './setSelectedProductChannel';
import { reducer as setSelectedProductVariant } from './setSelectedProductVariant';
import { reducer as setProductListingActiveTab } from './setProductListingActiveTab';
import { reducer as getListings } from './getListings';
import { reducer as getListingVariants } from './getListingVariants';
import { reducer as refreshListingVariantByMaster } from './refreshListingVariantByMaster';
// Listing draft creation and sync
import { reducer as createProductDraftFromMaster } from './createProductDraftFromMaster';
import { reducer as createVariantDraftFromMaster } from './createVariantDraftFromMaster';
import { reducer as createProductDraftFromLive } from './createProductDraftFromLive';
import { reducer as createVariantDraftFromLive } from './createVariantDraftFromLive';
import { reducer as createListingDraftFromPublished } from './createListingDraftFromPublished';
// Listing publish
import { reducer as publishProductDraft } from './publishProductDraft';
import { reducer as createAndPublishListingFromMaster } from './createAndPublishListingFromMaster';
import { reducer as publishSelectedVariantDrafts } from './publishSelectedVariantDrafts';
// Listing draft removal/discard
import { reducer as deleteProductDraft } from './deleteProductDraft';
import { reducer as discardProductDraft } from './discardProductDraft';
import { reducer as removeVariantFromDraft } from './removeVariantFromDraft';
import { reducer as removeVariantFromChannel } from './removeVariantFromChannel';
import { reducer as revertVariantDraftToPublished } from './revertVariantDraftToPublished';
// Listing live sync/removal
import { reducer as deleteProductLiveSnapshot } from './deleteProductLiveSnapshot';
import { reducer as removeProductLiveSnapshot } from './removeProductLiveSnapshot';
import { reducer as removeVariantLiveSnapshot } from './removeVariantLiveSnapshot';
import { reducer as deleteVariantLiveSnapshot } from './deleteVariantLiveSnapshot';
import { reducer as syncProductLive } from './syncProductLive';
// Listing field updates
import { reducer as updateListing } from './updateListing';
import { reducer as addListingKeywords } from './addListingKeywords';
import { reducer as removeListingKeywords } from './removeListingKeywords';
import { reducer as updateListingVariant } from './updateListingVariant';
import { registerBusyContextReducers } from './helpers/registerBusyContextReducers';
import { reducer as syncRegistryRenameOnMasters } from './helpers/syncRegistryRenameOnMasters';
import { reducer as stageListingProductsFromCreateDraft } from './stageListingProductsFromCreateDraft';

const initialState = {
  isLoaded: true,
  isLoading: false,
  isChangingOrder: false,
  items: [],
  filtered: null,
  isBusy: false,
  busyContext: null,
  searchQuery: '',
  nameCreatingProduct: null,
  error: null,
  search: {
    items: [],
    total: 0,
    isLoading: false,
    isPageLoading: false,
    error: null,
  },
  // sortType: null,
  sortTypeUpdating: false,
  entities: {
    listings: {},
    listingVariants: {},
    assets: {
      listingAssets: {},
      listingVariantAssets: {},
      masterAssets: {},
      masterVariantAssets: {},
    },
  },
  ui: {
    selectedChannelByProduct: {},
    activeTabByProductChannel: {},
    fetching: {
      listingAssets: {},
      listingVariantAssets: {},
      masterAssets: {},
      masterVariantAssets: {},
    },
  },
};

const productsReducer = createReducer(initialState, (builder) => {
  // Product loading and CRUD
  getProducts(builder);
  create(builder);
  search(builder);
  rename(builder);
  remove(builder);
  pullProduct(builder);
  pullProductFromMaster(builder);
  removeMasterProduct(builder);
  removeMasterVariant(builder);

  // Master product fields
  addKeywords(builder);
  removeKeywords(builder);
  archiveListing(builder);
  changeStatus(builder);
  changeDescription(builder);
  updateMasterVariant(builder);
  changeHandle(builder);
  changeOrder(builder);
  setUsageAltText(builder);
  patchAssetAltText(builder);
  updateMasterProduct(builder);
  changeProductCustomField(builder);
  changeVariantCustomField(builder);

  // Options and master variants
  getVariants(builder);
  createOption(builder);
  deleteOption(builder);
  createOptionValue(builder);
  deleteOptionValue(builder);
  createVariant(builder);

  // Product assets and media
  addAssets(builder);
  attachAssetsToTargets(builder);
  removeAssets(builder);
  detachAssetsFromTargets(builder);
  fetchAssets(builder);
  fetchVariantAssets(builder);
  removeAssetFromVariant(builder);
  detachProductVariantFromAssets(builder);
  fetchThumbnails(builder);
  updateAssetFields(builder);
  setMediaThumbnails(builder);
  fetchListingAssets(builder);

  // Channel/listing selection
  setSelectedProductChannel(builder);
  setSelectedProductVariant(builder);
  setProductListingActiveTab(builder);
  getListings(builder);
  getListingVariants(builder);
  refreshListingVariantByMaster(builder);

  // Listing draft creation and sync
  stageListingProductsFromCreateDraft(builder);
  createProductDraftFromMaster(builder);
  createVariantDraftFromMaster(builder);
  createProductDraftFromLive(builder);
  createVariantDraftFromLive(builder);
  createListingDraftFromPublished(builder);

  // Listing publish
  publishProductDraft(builder);
  createAndPublishListingFromMaster(builder);
  publishSelectedVariantDrafts(builder);

  // Listing draft removal/discard
  deleteProductDraft(builder);
  discardProductDraft(builder);
  removeVariantFromDraft(builder);
  removeVariantFromChannel(builder);
  revertVariantDraftToPublished(builder);

  // Listing live sync/removal
  deleteProductLiveSnapshot(builder);
  removeProductLiveSnapshot(builder);
  removeVariantLiveSnapshot(builder);
  deleteVariantLiveSnapshot(builder);
  syncProductLive(builder);

  // Listing field updates
  updateListing(builder);
  addListingKeywords(builder);
  removeListingKeywords(builder);
  updateListingVariant(builder);

  syncRegistryRenameOnMasters(builder);
  registerBusyContextReducers(builder);
});

export default productsReducer;
